import { Injectable, signal, computed } from '@angular/core';
import { fromEvent, merge, of, Observable } from 'rxjs';
import { map, startWith, distinctUntilChanged, debounceTime } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ConnectivityService {
  // Signals для реактивного состояния
  private _isOnline = signal<boolean>(navigator.onLine);
  private _lastOnlineTime = signal<Date | null>(navigator.onLine ? new Date() : null);
  private _lastOfflineTime = signal<Date | null>(!navigator.onLine ? new Date() : null);

  // Public readonly signals
  public readonly isOnline = this._isOnline.asReadonly();
  public readonly isOffline = computed(() => !this._isOnline());
  public readonly lastOnlineTime = this._lastOnlineTime.asReadonly();
  public readonly lastOfflineTime = this._lastOfflineTime.asReadonly();

  // Observable для подписки
  public readonly online$: Observable<boolean>;

  constructor() {
    // Создаём Observable из browser events
    this.online$ = merge(
      of(navigator.onLine),
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).pipe(
      startWith(navigator.onLine),
      debounceTime(300), // Защита от множественных переключений
      distinctUntilChanged()
    );

    // Подписываемся на изменения состояния
    this.online$.subscribe(isOnline => {
      this._isOnline.set(isOnline);

      if (isOnline) {
        this._lastOnlineTime.set(new Date());
      } else {
        this._lastOfflineTime.set(new Date());
      }
    });

    // Логируем начальное состояние
  }

  /**
   * Проверяет доступность интернета синхронно
   */
  checkOnlineStatus(): boolean {
    return navigator.onLine;
  }

  /**
   * Проверяет доступность конкретного URL (более надёжно чем navigator.onLine)
   */
  async checkServerConnectivity(url: string = '/api/health'): Promise<boolean> {
    if (!navigator.onLine) {
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

      const response = await fetch(url, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Возвращает время с последнего онлайн/оффлайн события
   */
  getTimeSinceLastChange(): { online?: number; offline?: number } {
    const result: { online?: number; offline?: number } = {};

    if (this._lastOnlineTime()) {
      result.online = Date.now() - this._lastOnlineTime()!.getTime();
    }

    if (this._lastOfflineTime()) {
      result.offline = Date.now() - this._lastOfflineTime()!.getTime();
    }

    return result;
  }

  /**
   * Форматирует время последнего изменения статуса
   */
  getFormattedLastChange(): string {
    if (this._isOnline()) {
      if (!this._lastOnlineTime()) return 'Только что подключено';

      const seconds = Math.floor((Date.now() - this._lastOnlineTime()!.getTime()) / 1000);

      if (seconds < 60) return 'Только что подключено';
      if (seconds < 3600) return `Онлайн ${Math.floor(seconds / 60)} мин`;
      return `Онлайн ${Math.floor(seconds / 3600)} ч`;
    } else {
      if (!this._lastOfflineTime()) return 'Нет соединения';

      const seconds = Math.floor((Date.now() - this._lastOfflineTime()!.getTime()) / 1000);

      if (seconds < 60) return 'Только что отключено';
      if (seconds < 3600) return `Оффлайн ${Math.floor(seconds / 60)} мин`;
      return `Оффлайн ${Math.floor(seconds / 3600)} ч`;
    }
  }

  /**
   * Показывает предупреждение если действие требует интернета
   */
  requireOnline(action: string = 'это действие'): boolean {
    if (!this._isOnline()) {
      return false;
    }
    return true;
  }
}
